import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/atoms/button';
import {
  DIALOG_VIEWPORT_ANCHOR_CLASS,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { Input } from '@/components/atoms/input';
import {
  useCreateWorkspace,
  useUpdateWorkspace,
} from '@/features/workspaces/services/workspace.api';
import { cn } from '@/libs/utils/cn';
import type { IWorkspace } from '@/types/api';
import type { ICreateWorkspaceDTO, IUpdateWorkspaceDTO } from '@/types/dto';

interface WorkspaceFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  workspace?: IWorkspace;
  onCreated?: (workspace: IWorkspace) => void;
  /**
   * Optional portal target. Omit so the dialog stays viewport-fixed and
   * stacks above overlays (map sidebar). Pass a container only when the
   * overlay must be scoped to that box.
   */
  dialogContainer?: HTMLElement;
}

interface FormValues {
  name: string;
}

export function WorkspaceFormModal(props: WorkspaceFormModalProps) {
  const { open, onOpenChange, mode, workspace, onCreated, dialogContainer } = props;

  const createWorkspace = useCreateWorkspace();
  const updateWorkspace = useUpdateWorkspace();

  const defaultValues = useMemo<FormValues>(
    () => ({
      name: mode === 'edit' ? (workspace?.name ?? '') : '',
    }),
    [mode, workspace],
  );

  const form = useForm<FormValues>({ defaultValues });

  const nameValue = form.watch('name');
  const isSubmitting =
    form.formState.isSubmitting || createWorkspace.isPending || updateWorkspace.isPending;

  useEffect(() => {
    // Re-seed on every open/close so an always-mounted modal still prefills
    // edit mode and starts create mode clean.
    form.reset(defaultValues);
  }, [open, defaultValues, form]);

  function handleDialogOpenChange(next: boolean) {
    onOpenChange(next);
    if (!next) {
      form.reset(defaultValues);
    }
  }

  async function handleSubmit(values: FormValues) {
    const trimmedName = values.name.trim();

    if (mode === 'edit' && workspace) {
      const dto: IUpdateWorkspaceDTO = {
        name: trimmedName,
      };

      try {
        await updateWorkspace.mutateAsync({ id: workspace.id, data: dto });
        onOpenChange(false);
        form.reset(defaultValues);
      } catch {
        // The useUpdateWorkspace hook owns the toast; keep the modal open.
      }

      return;
    }

    const dto: ICreateWorkspaceDTO = {
      name: trimmedName,
    };

    try {
      const created = await createWorkspace.mutateAsync(dto);
      onCreated?.(created);
      onOpenChange(false);
      form.reset(defaultValues);
    } catch {
      // The useCreateWorkspace hook owns the toast; keep the modal open.
    }
  }

  const isNameEmpty = !nameValue || nameValue.trim().length === 0;

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className={cn(
          'z-[80] max-w-lg',
          dialogContainer ? 'absolute' : DIALOG_VIEWPORT_ANCHOR_CLASS,
        )}
        overlayClassName={cn('z-[70]', dialogContainer && 'absolute inset-0')}
        dialogContainer={dialogContainer}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <DialogHeader className="gap-1">
              <DialogTitle className="text-primary-700 text-lg font-normal">
                {mode === 'edit' ? 'Edit workspace' : 'New workspace'}
              </DialogTitle>
            </DialogHeader>

            <FormField
              control={form.control}
              name="name"
              rules={{
                required: 'Name must be between 2 and 255 characters.',
                validate: (value: string) => {
                  const trimmed = value.trim();
                  return (
                    (trimmed.length >= 2 && trimmed.length <= 255) ||
                    'Name must be between 2 and 255 characters.'
                  );
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-primary-600 text-xs font-medium">Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Workspace name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" size="lg">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" size="lg" disabled={isSubmitting || isNameEmpty}>
                {isSubmitting ? 'Saving…' : mode === 'edit' ? 'Save' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
